import sodium from 'libsodium-wrappers';
import { SIGNATURE_PARAMS, HKDF_PARAMS } from './constants';

const webCryptoBuffer = (value: Uint8Array): ArrayBuffer =>
  value.slice().buffer as ArrayBuffer;

/**
 * A signing key pair (Ed25519) and a corresponding key-agreement key pair
 * (X25519) derived from the same seed material.
 *
 * Security rationale:
 * - Ed25519 is used for **digital signatures** (authenticity, non-repudiation).
 * - X25519 is used for **key agreement** (ECDH) to establish shared secrets.
 * - Both pairs are derived from the same 32-byte seed so that users only
 *   need to manage a single keypair identity. libsodium's
 *   `crypto_sign_seed_keypair` deterministically derives Ed25519 keys,
 *   and `crypto_sign_ed25519_pk_to_curve25519` / `_sk_to_curve25519`
 *   convert them to X25519 equivalents.
 */
export interface HybridKeyPair {
  /** Ed25519 public key for signature verification (32 bytes) */
  signingPublicKey: Uint8Array;
  /** Ed25519 secret key for signing (64 bytes: seed || public) */
  signingPrivateKey: Uint8Array;
  /** X25519 public key for ECDH (32 bytes) */
  encryptionPublicKey: Uint8Array;
  /** X25519 secret key for ECDH (32 bytes) */
  encryptionPrivateKey: Uint8Array;
}

/**
 * Generate a hybrid signing + encryption key pair from random seed material.
 *
 * Security rationale:
 * - A single 32-byte random seed deterministically derives both key pairs,
 *   reducing key management overhead while maintaining domain separation
 *   between signing (Ed25519) and key agreement (X25519).
 * - The Ed25519 → X25519 conversion uses the birational equivalence
 *   between the Edwards and Montgomery curves defined in RFC 7748.
 *   This is a well-studied mathematical mapping, not a custom derivation.
 *
 * @returns A {@link HybridKeyPair} with both signing and encryption keys
 */
export function generateKeyPair(): HybridKeyPair {
  const seed = sodium.randombytes_buf(SIGNATURE_PARAMS.seedBytes);

  // Ed25519 keypair for signing
  const edKeyPair = sodium.crypto_sign_seed_keypair(seed);

  // Convert to X25519 for ECDH
  const encPublicKey = sodium.crypto_sign_ed25519_pk_to_curve25519(
    edKeyPair.publicKey,
  );
  const encPrivateKey = sodium.crypto_sign_ed25519_sk_to_curve25519(
    edKeyPair.privateKey,
  );

  return {
    signingPublicKey: edKeyPair.publicKey,
    signingPrivateKey: edKeyPair.privateKey,
    encryptionPublicKey: encPublicKey,
    encryptionPrivateKey: encPrivateKey,
  };
}

/**
 * Sign a message with an Ed25519 private key.
 *
 * Security rationale:
 * - Ed25519 signatures provide 128-bit security level and are deterministic
 *   (no nonce to manage). The signature binds the signer's identity to the
 *   message content, providing both authenticity and integrity.
 * - This is used to sign envelope metadata so that the recipient can
 *   verify the sender's identity before attempting decryption.
 *
 * @param message    - The data to sign (arbitrary bytes)
 * @param privateKey - The 64-byte Ed25519 secret key
 * @returns A 64-byte Ed25519 signature
 */
export function sign(
  message: Uint8Array,
  privateKey: Uint8Array,
): Uint8Array {
  return sodium.crypto_sign_detached(message, privateKey);
}

/**
 * Verify an Ed25519 signature.
 *
 * Security rationale:
 * - Verification is performed in constant time to prevent timing
 *   side-channel attacks against the signer's public key.
 * - Returns `false` (rather than throwing) on invalid signatures to
 *   avoid leaking information through exception handling paths.
 *
 * @param message   - The original message that was signed
 * @param signature - The 64-byte Ed25519 signature to verify
 * @param publicKey - The 32-byte Ed25519 public key of the signer
 * @returns `true` if the signature is valid, `false` otherwise
 */
export function verify(
  message: Uint8Array,
  signature: Uint8Array,
  publicKey: Uint8Array,
): boolean {
  return sodium.crypto_sign_verify_detached(signature, message, publicKey);
}

/**
 * Perform X25519 Elliptic-Curve Diffie-Hellman key agreement.
 *
 * Security rationale:
 * - X25519 (Curve25519) provides 128-bit security with a Montgomery
 *   ladder that is naturally constant-time, immune to timing attacks.
 * - The raw 32-byte shared secret is NOT safe to use directly as a
 *   symmetric key — it must be passed through {@link deriveSharedKey}
 *   via HKDF to produce a uniformly distributed key.
 *
 * @param privateKey      - The caller's 32-byte X25519 private key
 * @param theirPublicKey  - The other party's 32-byte X25519 public key
 * @returns A 32-byte raw shared secret (needs HKDF before use)
 */
export function ecdhAgreement(
  privateKey: Uint8Array,
  theirPublicKey: Uint8Array,
): Uint8Array {
  return sodium.crypto_scalarmult(privateKey, theirPublicKey);
}

/**
 * Derive a symmetric key from an ECDH shared secret using HKDF-SHA256.
 *
 * Security rationale:
 * - Raw ECDH output is a group element, not a uniformly random bit string.
 *   HKDF (RFC 5869) extracts and expands the entropy into a proper
 *   cryptographic key with uniform distribution.
 * - The `info` parameter binds the derived key to its specific context
 *   (e.g. "envelope-dek-encryption"), preventing key reuse across
 *   different cryptographic operations.
 *
 * @param sharedSecret - The 32-byte raw output of {@link ecdhAgreement}
 * @param info         - Context string for domain separation
 * @returns A 32-byte Uint8Array suitable for use as a symmetric key
 */
export async function deriveSharedKey(
  sharedSecret: Uint8Array,
  info: string,
): Promise<Uint8Array> {
  // Use the first 32 bytes of the shared secret as the HKDF salt
  // (this is acceptable because the shared secret is already uniformly
  // distributed enough for an HKDF salt).
  const salt = sharedSecret.slice(0, HKDF_PARAMS.saltBytes);

  const infoBytes = new TextEncoder().encode(
    `${HKDF_PARAMS.infoPrefix}:${info}`,
  );

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    webCryptoBuffer(sharedSecret),
    { name: 'HKDF' },
    false,
    ['deriveBits'],
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: HKDF_PARAMS.algorithm,
      salt: webCryptoBuffer(salt),
      info: webCryptoBuffer(infoBytes),
    },
    keyMaterial,
    256, // 32 bytes
  );

  return new Uint8Array(derivedBits);
}
